import { useState, useEffect } from 'react'
import { useAuth } from '@/contexts/AuthContext'
import { RedFlagsPanel } from '@/components/alerts/RedFlagsPanel'
import { AlertsWidget } from '@/components/alerts/AlertsWidget'
import { RedFlag, Alert } from '@/types'
import { aiService, AIAnomalyResponse } from '@/services/aiService'
import { apiClient } from '@/services/apiClient'

export function AlertsPage() {
  const { user } = useAuth()
  const [redFlags, setRedFlags] = useState<RedFlag[]>([])
  const [alerts, setAlerts] = useState<Alert[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fetchAlerts = async () => {
      if (!user?.businessId) {
        setLoading(false)
        return
      }

      setLoading(true)
      setError(null)

      try {
        // Fetch business score data to generate score-based alerts
        let scoreData: any = null
        try {
          const scoreRes = await apiClient.get<any>(`/scores/${user.businessId}`)
          scoreData = scoreRes.data
        } catch {
          // Score might not exist yet
        }

        // Fetch AI anomaly data
        let anomalyData: AIAnomalyResponse | null = null
        try {
          anomalyData = await aiService.getAnomaly(user.businessId)
        } catch {
          // AI anomaly endpoint might not be available
        }

        // Build real alerts based on actual data
        const generatedAlerts: Alert[] = []
        const generatedFlags: RedFlag[] = []

        // Score-based alerts
        if (scoreData) {
          const score = scoreData.score

          if (score < 40) {
            generatedAlerts.push({
              id: 'score-critical',
              businessId: user.businessId,
              type: 'score_change',
              severity: 'critical',
              title: 'Critical Risk Score',
              message: `Your business score is ${score.toFixed(1)} — this indicates critical risk. Immediate attention is recommended.`,
              data: { score },
              read: false,
              actionRequired: true,
              actionUrl: '/recommendations',
              createdAt: scoreData.calculatedAt || new Date().toISOString(),
            })
          } else if (score < 60) {
            generatedAlerts.push({
              id: 'score-medium',
              businessId: user.businessId,
              type: 'score_change',
              severity: 'warning',
              title: 'Medium Risk Detected',
              message: `Your business score is ${score.toFixed(1)} — below the safe threshold of 60. Consider reviewing recommendations to improve.`,
              data: { score },
              read: false,
              actionRequired: true,
              actionUrl: '/recommendations',
              createdAt: scoreData.calculatedAt || new Date().toISOString(),
            })
          } else if (score >= 80) {
            generatedAlerts.push({
              id: 'score-excellent',
              businessId: user.businessId,
              type: 'score_change',
              severity: 'info',
              title: 'Excellent Business Health',
              message: `Your business score is ${score.toFixed(1)} — great job maintaining a healthy business profile.`,
              data: { score },
              read: true,
              actionRequired: false,
              createdAt: scoreData.calculatedAt || new Date().toISOString(),
            })
          }

          // Band-related alert
          if (scoreData.band === 'C' || scoreData.band === 'D') {
            generatedAlerts.push({
              id: 'band-warning',
              businessId: user.businessId,
              type: 'band_change',
              severity: 'warning',
              title: `Score Band: ${scoreData.band}`,
              message: `Your business is in band ${scoreData.band}. Improving your score above 70 will move you to a better band.`,
              data: { band: scoreData.band },
              read: false,
              actionRequired: true,
              actionUrl: '/score',
              createdAt: scoreData.calculatedAt || new Date().toISOString(),
            })
          }
        }

        // AI anomaly-based alerts/red flags
        if (anomalyData?.data) {
          const anomaly = anomalyData.data

          if (anomaly.is_anomaly) {
            generatedFlags.push({
              id: 'anomaly-detected',
              type: 'transaction_anomaly',
              severity: anomaly.severity,
              title: `Anomaly Detected: ${anomaly.anomaly_type || 'Unusual Pattern'}`,
              description: `AI analysis detected an anomaly with a score of ${(anomaly.anomaly_score * 100).toFixed(0)}%. ${
                anomaly.contributing_features?.length
                  ? `Top contributing factors: ${anomaly.contributing_features.slice(0, 3).map(f => f.feature).join(', ')}.`
                  : ''
              }`,
              impact: anomaly.severity === 'critical' ? -8 : anomaly.severity === 'high' ? -5 : anomaly.severity === 'medium' ? -3 : -1,
              detectedAt: anomalyData.timestamp || new Date().toISOString(),
              status: 'active',
              evidence: {
                anomalyScore: anomaly.anomaly_score,
                anomalyType: anomaly.anomaly_type,
                contributingFeatures: anomaly.contributing_features,
              },
            })

            generatedAlerts.push({
              id: 'anomaly-alert',
              businessId: user.businessId,
              type: 'red_flag',
              severity: anomaly.severity === 'critical' ? 'critical' : anomaly.severity === 'high' ? 'error' : 'warning',
              title: 'AI Anomaly Detection Alert',
              message: `An anomaly of type "${anomaly.anomaly_type || 'unknown'}" was detected with ${anomaly.severity} severity. Review the red flags section for details.`,
              data: { anomalyScore: anomaly.anomaly_score },
              read: false,
              actionRequired: true,
              createdAt: anomalyData.timestamp || new Date().toISOString(),
            })
          }
        }

        // If no anomaly is found, add a positive note
        if (anomalyData?.data && !anomalyData.data.is_anomaly) {
          generatedAlerts.push({
            id: 'no-anomaly',
            businessId: user.businessId,
            type: 'system_notification',
            severity: 'info',
            title: 'No Anomalies Detected',
            message: 'AI analysis found no unusual patterns in your business data. Your operations appear normal.',
            read: true,
            actionRequired: false,
            createdAt: anomalyData.timestamp || new Date().toISOString(),
          })
        }

        setAlerts(generatedAlerts)
        setRedFlags(generatedFlags)
      } catch (err: any) {
        setError(err.message || 'Failed to load alerts')
      } finally {
        setLoading(false)
      }
    }

    fetchAlerts()
  }, [user?.businessId])

  const handleMarkAsRead = (alertId: string) => {
    setAlerts(prev => prev.map(alert => 
      alert.id === alertId ? { ...alert, read: true } : alert
    ))
  }

  const handleDismissAlert = (alertId: string) => {
    setAlerts(prev => prev.filter(alert => alert.id !== alertId))
  }

  const activeRedFlags = redFlags.filter(flag => flag.status === 'active')
  const unreadAlerts = alerts.filter(alert => !alert.read)
  const criticalIssues = [
    ...redFlags.filter(flag => flag.severity === 'critical' && flag.status === 'active'),
    ...alerts.filter(alert => alert.severity === 'critical' && !alert.read)
  ]

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <div className="animate-pulse">
            <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-1/4 mb-4"></div>
            <div className="space-y-3">
              <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4"></div>
              <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2"></div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // No business linked state
  if (!user?.businessId) {
    return (
      <div className="space-y-6">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 text-center">
          <div className="w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-2xl">🔔</span>
          </div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">No Business Linked</h2>
          <p className="text-gray-500 dark:text-gray-400">
            Complete your business profile to see alerts and anomaly detection results.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              Alerts & Red Flags
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mt-2">
              Monitor issues that may impact your TLBH score and business health
            </p>
          </div>
          <div className="flex items-center space-x-4">
            {criticalIssues.length > 0 && (
              <div className="flex items-center space-x-2 text-red-600 dark:text-red-400">
                <span className="text-xl">🚨</span>
                <span className="font-medium">{criticalIssues.length} Critical</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Error banner */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Active Red Flags</p>
              <p className="text-3xl font-bold text-gray-900 dark:text-white">{activeRedFlags.length}</p>
            </div>
            <div className="w-12 h-12 bg-red-100 dark:bg-red-900 rounded-lg flex items-center justify-center">
              <span className="text-red-600 dark:text-red-400 text-xl">🚩</span>
            </div>
          </div>
          <div className="mt-4">
            <div className="flex items-center text-sm">
              <span className={`${
                activeRedFlags.length > 0 ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'
              }`}>
                {activeRedFlags.length > 0 ? 'Needs attention' : 'All clear'}
              </span>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Unread Alerts</p>
              <p className="text-3xl font-bold text-gray-900 dark:text-white">{unreadAlerts.length}</p>
            </div>
            <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900 rounded-lg flex items-center justify-center">
              <span className="text-blue-600 dark:text-blue-400 text-xl">📬</span>
            </div>
          </div>
          <div className="mt-4">
            <div className="flex items-center text-sm">
              <span className="text-blue-600 dark:text-blue-400">
                {unreadAlerts.length} new notifications
              </span>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Critical Issues</p>
              <p className="text-3xl font-bold text-gray-900 dark:text-white">{criticalIssues.length}</p>
            </div>
            <div className="w-12 h-12 bg-orange-100 dark:bg-orange-900 rounded-lg flex items-center justify-center">
              <span className="text-orange-600 dark:text-orange-400 text-xl">⚠️</span>
            </div>
          </div>
          <div className="mt-4">
            <div className="flex items-center text-sm">
              <span className={`${
                criticalIssues.length > 0 ? 'text-orange-600 dark:text-orange-400' : 'text-green-600 dark:text-green-400'
              }`}>
                {criticalIssues.length > 0 ? 'Immediate action required' : 'No critical issues'}
              </span>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Alerts</p>
              <p className="text-3xl font-bold text-gray-900 dark:text-white">{alerts.length}</p>
            </div>
            <div className="w-12 h-12 bg-green-100 dark:bg-green-900 rounded-lg flex items-center justify-center">
              <span className="text-green-600 dark:text-green-400 text-xl">📋</span>
            </div>
          </div>
          <div className="mt-4">
            <div className="flex items-center text-sm">
              <span className="text-green-600 dark:text-green-400">
                {alerts.length === 0 ? 'No active alerts' : `${alerts.length} total`}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* No alerts state */}
      {alerts.length === 0 && redFlags.length === 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-8 text-center">
          <div className="w-16 h-16 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-green-600 dark:text-green-400 text-2xl">✓</span>
          </div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            No Active Alerts
          </h3>
          <p className="text-gray-500 dark:text-gray-400">
            Your business is in good standing. No anomalies or issues have been detected.
          </p>
        </div>
      )}

      {/* Main Content */}
      {(alerts.length > 0 || redFlags.length > 0) && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Red Flags Panel */}
          <div className="lg:col-span-2">
            <RedFlagsPanel 
              redFlags={redFlags}
              alerts={alerts}
              showFilters={true}
            />
          </div>

          {/* Alerts Widget */}
          <div>
            <AlertsWidget 
              alerts={alerts}
              maxItems={10}
              onMarkAsRead={handleMarkAsRead}
              onDismiss={handleDismissAlert}
            />
          </div>
        </div>
      )}

      {/* Quick Actions */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Quick Actions
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <button className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-left">
            <h3 className="font-medium text-gray-900 dark:text-white">Review Red Flags</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Investigate and resolve active red flags
            </p>
          </button>
          <button className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-left">
            <h3 className="font-medium text-gray-900 dark:text-white">Update Business Data</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Ensure all business information is current
            </p>
          </button>
          <button className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-left">
            <h3 className="font-medium text-gray-900 dark:text-white">View Recommendations</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              See how to improve your score
            </p>
          </button>
        </div>
      </div>
    </div>
  )
}
